import SaveIcon from '@mui/icons-material/Save';
import { useTheme } from '@mui/material';
import { LinkedinTypes, ProductGroups } from 'enums';
import { Funnel, FunnelFunctionFlow, FunnelFunctions, NodeVariants } from 'features/co-pilot/co-pilot.types';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import PaiIconButton from 'features/ui/base-components/pai-icon-button';
import _ from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Flow, {
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  BackgroundVariant,
  ConnectionLineType,
  Edge,
  EdgeChange,
  Node,
  NodeChange,
  NodeTypes,
  Panel,
  useEdgesState,
  useNodesState,
  useReactFlow,
  useUpdateNodeInternals,
  Viewport,
} from 'reactflow';
import 'reactflow/dist/style.css';
import useBottomNodeMenuStore from 'stores/bottom-node-menu-store';
import useCampaignFlowStore from 'stores/campaign-flow-store';
import useCampaignNodeStore from 'stores/campaign-node-store';
import useTopNodeMenuStore from 'stores/top-node-menu-store';
import { makeStyles } from 'tss-react/mui';
import { findConnectedNodes, numberFunctionNodes, resolveOverlaps } from 'utils/flowNode.utils';
import { v4 as uuidv4 } from 'uuid';
import addButtonEdge from './add-button-edge';
import BottomNodeMenu from './bottom-node-menu';
import CampaignStatusPanel from './campaign-status-panel';
import ConditionalNode from './conditional-node';
import FlowToolbar from './flow-toolbar';
import InitialFlowInstruction from './initial-flow-instruction';
import MessageNode from './message-node';
import TopNodeMenu from './top-node-menu';
import UtilityNode from './utility-node';
import WaitingTimeNode from './waiting-time-node';

const nodeTypes: NodeTypes = {
  functionNode: MessageNode,
  conditionalNode: ConditionalNode,
  conditionWithDurationNode: ConditionalNode,
  waitNode: WaitingTimeNode,
  utilityNode: UtilityNode,
};

const connectionLineStyle = { stroke: '#000' };

const viewport: Viewport = { x: 0, y: 0, zoom: 1 };

const edgeTypes = {
  buttonedge: addButtonEdge,
};

interface Props {
  expanded: boolean;

  selectedFunnelFunctionsLibrary: string;

  productGroup: ProductGroups;
  campaign: Funnel;
  linkedinAccount: LinkedinAccount | undefined;
  isUpdating: boolean;

  handleSelectedFunnelFunctionsLibraryChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleUpdateCampaignReactFlow(flow: FunnelFunctionFlow, signal: AbortSignal): void;
}

const CampaignFlow: React.FC<Props> = ({
  expanded,
  selectedFunnelFunctionsLibrary,
  productGroup,
  campaign,
  linkedinAccount,
  isUpdating,
  handleSelectedFunnelFunctionsLibraryChange,
  handleUpdateCampaignReactFlow,
}) => {
  const { classes } = useStyles({ expanded });
  const { t } = useTranslation();
  const theme = useTheme();

  const { reactFlow } = campaign;
  const defaultNodes = reactFlow?.nodes || [];
  const defaultEdges = reactFlow?.edges || [];
  const defaultViewport = reactFlow?.viewport || viewport;

  const { isFlowChanging, setIsFlowChanging } = useCampaignFlowStore((state) => ({
    isFlowChanging: state.isFlowChanging,
    setIsFlowChanging: state.setIsFlowChanging,
  }));
  const isPromptChanging = useCampaignNodeStore((state) => state.isPromptChanging);

  const [nodes, setNodes] = useNodesState(defaultNodes);
  const [edges, setEdges] = useEdgesState(defaultEdges);
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);
  const [zoomMode, setZoomMode] = useState(true);

  const { getViewport, getEdges, zoomIn, zoomOut, fitView } = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();

  const controllersRef = useRef(new Map<string, AbortController>());
  const debouncedSaveRef = useRef<_.DebouncedFunc<() => Promise<void>>>();
  const didMountRef = useRef(false);
  const prevPromptChangingRef = useRef(isPromptChanging);

  const createAbortController = (key: string) => {
    if (controllersRef.current.has(key)) {
      controllersRef.current.get(key)?.abort();
    }

    const controller = new AbortController();
    controllersRef.current.set(key, controller);
    return controller;
  };

  useEffect(() => {
    return () => {
      setReactFlowInstance(null);
      setNodes([]);
      setEdges([]);
    };
  }, [campaign._id]);

  const {
    menuOpen: bottomMenuOpen,
    anchorEl: bottomAnchorEl,
    handleCloseMenu: handleCloseBottomMenu,
  } = useBottomNodeMenuStore((state) => ({
    menuOpen: state.menuOpen,
    anchorEl: state.anchorEl,
    handleCloseMenu: state.handleCloseMenu,
  }));

  const {
    menuOpen: topMenuOpen,
    anchorEl: topAnchorEl,
    handleCloseMenu: handleCloseTopMenu,
  } = useTopNodeMenuStore((state) => ({
    menuOpen: state.menuOpen,
    anchorEl: state.anchorEl,
    handleCloseMenu: state.handleCloseMenu,
  }));

  const allMessagesHaveType = nodes.filter((node) => node.type === NodeVariants.FUNCTION_NODE).every((node) => node.data?.functionName);
  const allConditionsHaveType = nodes
    .filter((node) => node.type === NodeVariants.CONDITIONAL_NODE || node.type === NodeVariants.CONDITION_WITH_DURATION_NODE)
    .every((node) => node.data?.functionName);

  const inMailNodes = nodes.filter(
    (node) =>
      node.type === NodeVariants.FUNCTION_NODE &&
      (node.data.functionName === FunnelFunctions.SEND_IN_MAIL || node.data.functionName === FunnelFunctions.SEND_IN_MAIL_WITH_CONTEXT)
  );

  const emailNodes = nodes.filter(
    (node) =>
      node.type === NodeVariants.FUNCTION_NODE &&
      (node.data.functionName === FunnelFunctions.SEND_EMAIL ||
        node.data.functionName === FunnelFunctions.SEND_EMAIL_WITH_CONTEXT ||
        node.data.functionName === FunnelFunctions.SEND_FOLLOW_UP_EMAIL)
  );

  const inMailsAreAllowed = () => {
    if (_.isEmpty(inMailNodes)) return true;

    if (linkedinAccount?.liProductType === LinkedinTypes.LINKEDIN) return false;

    return true;
  };

  const emailsAreAllowed = () => {
    if (_.isEmpty(emailNodes)) return true;

    if (_.isNil(linkedinAccount)) return false;

    const configOk = !_.isNil(linkedinAccount.emailConfig) && !linkedinAccount.emailConfig.accessRevoked;

    const accountsOk = (linkedinAccount?.emailAccounts ?? []).some((ea) => !ea.accessRevoked);

    return configOk || accountsOk;
  };

  /**
   * Campaign flow handlers
   */

  const handleSaveCampaignFlow = useCallback(async () => {
    if (!reactFlowInstance) {
      window.alert(t('flowNotFound'));
      setIsFlowChanging(false);
      return;
    }

    const controller = createAbortController('saveCampaignFlow');

    const flow = reactFlowInstance.toObject();
    handleUpdateCampaignReactFlow(flow, controller.signal);
    setIsFlowChanging(false);
  }, [reactFlowInstance]);

  useEffect(() => {
    debouncedSaveRef.current = _.debounce(handleSaveCampaignFlow, 1000);
    return () => {
      if (debouncedSaveRef.current) {
        debouncedSaveRef.current.cancel();
      }
      setIsFlowChanging(false);
    };
  }, [handleSaveCampaignFlow]);

  useEffect(() => {
    return () => {
      if (debouncedSaveRef.current) {
        debouncedSaveRef.current.cancel();
      }
      if (controllersRef.current) {
        controllersRef.current.forEach((controller) => controller.abort());
      }

      setIsFlowChanging(false);
    };
  }, []);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      const edges = getEdges();

      setNodes((oldNodes) => {
        const newNodes = applyNodeChanges(changes, oldNodes);
        const resolvedNodes = resolveOverlaps(newNodes);
        const numberedNodes = numberFunctionNodes(resolvedNodes, edges);
        return numberedNodes;
      });

      setIsFlowChanging(true);

      if (reactFlowInstance) {
        debouncedSaveRef.current?.();
      } else {
        setIsFlowChanging(false);
      }
    },
    [setNodes, handleSaveCampaignFlow]
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      setEdges((oldEdges) => applyEdgeChanges(changes, oldEdges));

      setIsFlowChanging(true);

      if (reactFlowInstance) {
        debouncedSaveRef.current?.();
      } else {
        setIsFlowChanging(false);
      }
    },
    [setEdges, handleSaveCampaignFlow, reactFlowInstance]
  );

  // Trigger save when the user finishes dragging a node (for better reliability)
  const handleNodeDragStop = () => {
    setIsFlowChanging(true);
    if (reactFlowInstance) {
      debouncedSaveRef.current?.();
    }
  };

  const handleAddBottomNode = (
    newNodeVariant: NodeVariants,
    sourceNodeId?: string | null,
    sourceNodePosition?: { x: number; y: number } | null,
    sourceNodeHeight: number = 225,
    sourceNodePath?: 'success' | 'failure' | null
  ) => {
    const viewport = getViewport();
    const { x: viewportX, y: viewportY, zoom: viewportZoom } = viewport;

    const newNodeId = uuidv4();

    const newPosition = !_.isNil(sourceNodePosition)
      ? {
        x: sourceNodePosition.x + (!_.isNil(sourceNodePath) ? (sourceNodePath === 'success' ? -300 : 300) : 0),
        y: sourceNodePosition.y + (sourceNodeHeight + 84) + (!_.isNil(sourceNodePath) ? 48 : 0),
      }
      : { x: (450 - viewportX) / viewportZoom, y: (50 - viewportY) / viewportZoom };

    let targetNodeId: string | null = null;

    const newNode: Node = {
      id: newNodeId,
      type: newNodeVariant,
      position: newPosition,
      deletable: false,
      selectable: false,
      data: { isStartNode: _.isEmpty(nodes) },
    };

    if (newNodeVariant === NodeVariants.WAIT_NODE) {
      newNode.data.waitTillNext = 2 * 60 * 60;
      newNode.data.functionName = FunnelFunctions.WAIT_STEP;
    }

    if (newNodeVariant === NodeVariants.UTILITY_NODE) {
      newNode.data.functionName = FunnelFunctions.WITHDRAW_CONNECTION_REQUEST;
    }

    setNodes((nds) => nds.concat(newNode));

    if (sourceNodeId) {
      let existingEdge;

      if (!_.isNil(sourceNodePath)) {
        existingEdge = edges.find((edge) => edge.source === sourceNodeId && edge.sourceHandle === sourceNodePath);
      } else {
        existingEdge = edges.find((edge) => edge.source === sourceNodeId);
      }

      if (existingEdge) {
        const sourceNode = nodes.find((node) => node.id === sourceNodeId);
        const targetNode = nodes.find((node) => node.id === existingEdge.target);
        const newNodeHeight =
          newNodeVariant === NodeVariants.FUNCTION_NODE ? 142 : newNodeVariant === NodeVariants.WAIT_NODE ? 173 : sourceNodeHeight;

        if (sourceNode && targetNode) {
          targetNodeId = targetNode.id;

          setNodes((nds) =>
            nds.map((node) => {
              if (findConnectedNodes(targetNodeId!, edges).includes(node.id)) {
                return {
                  ...node,
                  position: {
                    ...node.position,
                    y: node.position.y + newNodeHeight + 84,
                  },
                };
              }
              return node;
            })
          );

          setEdges((eds) => eds.filter((edge) => edge.id !== existingEdge.id));
        }

        if (targetNodeId) {
          const edgeFromNewNode: Edge = {
            id: uuidv4(),
            source: newNodeId,
            target: targetNodeId,
            type: 'buttonedge',
            deletable: false,
            style: { stroke: theme.palette.greys.medium },
          };

          setEdges((eds) => eds.concat(edgeFromNewNode));
        }
      }

      const edgeToNewNode: Edge = {
        id: uuidv4(),
        source: sourceNodeId,
        sourceHandle: sourceNodePath,
        target: newNodeId,
        type: 'buttonedge',
        deletable: false,
        style: { stroke: theme.palette.greys.medium },
      };

      setEdges((eds) => eds.concat(edgeToNewNode));
    }

    updateNodeInternals([sourceNodeId, newNodeId, targetNodeId].filter(Boolean) as string[]);
  };

  const handleAddTopNode = (
    newNodeVariant: NodeVariants,
    targetNodeId: string,
    targetNodePosition: { x: number; y: number },
    targetNodeHeight: number,
    targetNodePath: 'success' | 'failure'
  ) => {
    const newNodeId = uuidv4();

    const newPosition = {
      x: targetNodePosition.x + (targetNodePath === 'success' ? 300 : -300),
      y: targetNodePosition.y - targetNodeHeight - 84 - 48,
    };

    const newNode: Node = {
      id: newNodeId,
      type: newNodeVariant,
      position: newPosition,
      deletable: false,
      selectable: false,
      data: { isStartNode: true },
    };

    setNodes((nds) => nds.concat(newNode));

    const edgeToNewNode: Edge = {
      id: uuidv4(),
      source: newNodeId,
      sourceHandle: targetNodePath,
      target: targetNodeId,
      type: 'buttonedge',
      deletable: false,
      style: { stroke: theme.palette.greys.medium },
    };

    setEdges((eds) => eds.concat(edgeToNewNode));

    updateNodeInternals([newNodeId, targetNodeId]);
  };

  /**
   * Mode handlers
   */

  const handleZoomModeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setZoomMode(e.target.checked);
  };

  // Auto-save when nodes or edges are changed programmatically (e.g., editing a node field)
  useEffect(() => {
    if (!didMountRef.current) {
      // Skip the first render where nodes/edges are initialised
      didMountRef.current = true;
      return;
    }

    setIsFlowChanging(true);

    if (reactFlowInstance) {
      debouncedSaveRef.current?.();
    } else {
      setIsFlowChanging(false);
    }
  }, [nodes, edges]);

  useEffect(() => {
    if (prevPromptChangingRef.current && !isPromptChanging) {
      // Editing finished – schedule save
      setIsFlowChanging(true);
      if (reactFlowInstance) {
        debouncedSaveRef.current?.();
      } else {
        setIsFlowChanging(false);
      }
    }
    prevPromptChangingRef.current = isPromptChanging;
  }, [isPromptChanging]);

  // If there are pending changes and the React Flow instance just initialized, ensure we persist them.
  useEffect(() => {
    if (reactFlowInstance && isFlowChanging) {
      debouncedSaveRef.current?.();
    }
  }, [reactFlowInstance]);

  return (
    <div className={classes.flowContainer}>
      <Flow
        className={classes.flow}
        nodes={nodes}
        edges={edges}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        connectionLineStyle={connectionLineStyle}
        connectionLineType={ConnectionLineType.Step}
        onInit={setReactFlowInstance}
        onNodeDragStop={handleNodeDragStop}
        defaultViewport={defaultViewport}
        zoomOnScroll={zoomMode}
        panOnScroll={!zoomMode}
        maxZoom={10}
        minZoom={0.1}
      >
        {_.isEmpty(nodes) ? (
          <Panel position="top-center">
            <InitialFlowInstruction
              selectedFunnelFunctionsLibrary={selectedFunnelFunctionsLibrary}
              handleSelectedFunnelFunctionsLibraryChange={handleSelectedFunnelFunctionsLibraryChange}
            />
          </Panel>
        ) : null}
        {!_.isNil(campaign) ? (
          <Panel position={'top-left'}>
            <CampaignStatusPanel
              productGroup={productGroup}
              allMessagesHaveType={allMessagesHaveType}
              allConditionsHaveType={allConditionsHaveType}
              inMailsAreAllowed={inMailsAreAllowed}
              emailsAreAllowed={emailsAreAllowed}
            />
          </Panel>
        ) : null}
        <Panel position="top-right">
          <PaiIconButton
            onClick={handleSaveCampaignFlow}
            disabled={_.isNil(reactFlowInstance) || isUpdating || isFlowChanging || _.isEmpty(nodes)}
          >
            <SaveIcon />
          </PaiIconButton>
        </Panel>
        <Panel position="bottom-left">
          <FlowToolbar zoomIn={zoomIn} zoomOut={zoomOut} fitView={fitView} zoomMode={zoomMode} handleZoomModeChange={handleZoomModeChange} />
        </Panel>
        <Background className={classes.flowBackground} variant={BackgroundVariant.Dots} color={'#DDD'} gap={18} size={1} />
      </Flow>

      <BottomNodeMenu
        anchorEl={bottomAnchorEl}
        menuOpen={bottomMenuOpen}
        handleAddNode={handleAddBottomNode}
        handleCloseMenu={handleCloseBottomMenu}
      />
      <TopNodeMenu anchorEl={topAnchorEl} menuOpen={topMenuOpen} handleAddNode={handleAddTopNode} handleCloseMenu={handleCloseTopMenu} />
    </div>
  );
};

const useStyles = makeStyles<{ expanded: boolean }>()((theme, { expanded }) => ({
  flowContainer: {
    height: '100%',
    minWidth: 0,
    fontSize: theme.typography.pxToRem(14),
    flex: !expanded ? '1 1 auto' : '0 0 0%',
    background: '#FAFAFC',
    // transition: '250ms',
  },
  flow: {
    '& .react-flow__node-functionNode': {
      padding: '0px',
      border: 'none',
    },
  },
  flowBackground: {
    backgroundColor: '#FAFAFC',
  },
}));

export default CampaignFlow;
